import React, { useState, useEffect, useContext } from "react";
import { useRolePermissions } from "../Settings/RoleContext";
import { useParams } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import api from "../../api/axiosConfig";
import DatePicker from "react-datepicker";
import { MultiSelect } from "react-multi-select-component";
import "../../styles/layouts/ProjectLayout.css";
import { ProjectContext } from "../../App";
import { ToastContainer, toast } from "react-toastify";

const ProjectLayout = () => {
  const [openSection, setOpenSection] = useState("projectInformation");
  const { hasPermission } = useRolePermissions();
  const toggleSection = (section) => {
    setOpenSection(openSection === section ? section : section);
  };
  const { projectId } = useParams();
  const { onFrameButtonClick, isNavBarOpen } = useContext(ProjectContext);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Planning");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedCompletedDate, setSelectedCompletedDate] = useState("");
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [groupOptions, SetGroupOptions] = useState([]);
  const options = groupOptions.map((group) => ({
    label: group.code,
    value: group.id,
  }));
  const [selectedOptions, setSelectedOptions] = useState([]);
  useEffect(() => {
    const projectGroups = project?.associated_groups || [];
    const mappedGroups = projectGroups.map((group) => ({
      label: group.name,
      value: group.id,
    }));
    setSelectedOptions(mappedGroups);
  }, [project]);
  const [associatedUsers, setAssociatedUsers] = useState([]);
  const [billingMethod, setBillingMethod] = useState("");
  const [budgetType, setBudgetType] = useState("");
  const [currencyType, setCurrencyType] = useState("");
  const [statusOptions, setStatusOption] = useState([]);
  const [projectStatus, setProjectStatus] = useState("");
  const [statusColor, setStatusColor] = useState("");
  const [projectNo, setProjectNo] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  useEffect(() => {
    if (customerName) {
      const customer = customers.find((c) => c.id === customerName);
      if (customer) {
        setCustomerId(customer.customerno); // Accessing 'customerno' from the matched object
      } else {
        console.warn("Customer not found for ID:", customerName);
      }
    }
  }, [customers, customerName]);
  const handleGroupChange = (selected) => {
    setSelectedOptions(selected);
  };
  const handleBlur = async () => {
    try {
      // Call the API to update the selected groups using the PUT method
      await updateDetails(selectedOptions);
    } catch (error) {
      console.error("Error updating details:", error.message);
    }
  };

  const updateDetails = async (selectedOptions) => {
    try {
      const response = await api.put(
        `/api/projects/${projectId}/update-groups`,
        { groups: selectedOptions } // Send selected options as groups
      );
      if (!response.ok) {
        throw new Error("Failed to update groups");
      }
    } catch (error) {
      console.error("Error updating groups:", error);
    }
  };
  //fetching status options, group , project members
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/api/status");
        const groupres = await api.get(`/api/groups`);
        const cusres = await api.get(`/api/customers`);
        const associateUsersres = await api.get(
          `/api/projects/${projectId}/users`
        );
        setCustomers(cusres.data);

        if (Array.isArray(res.data)) {
          const statusData = res.data.map((status) => ({
            id: status.id,
            name: status.name,
            color: status.color,
          }));
          setStatusOption(statusData);
        } else {
          console.error("Expected an array of statuses, but got:", res.data);
          setStatusOption([]); // Fallback to an empty array
        }

        // Ensure that groupres.data is an array
        if (Array.isArray(groupres.data)) {
          const mappedGroups = groupres.data.map((group) => ({
            id: group.id,
            code: group.code,
            name: group.name,
            description: group.description || "No description available", // Handle empty descriptions
            managerId: group.manager_id,
            managerName: group.manager_user_name,
            managerDesignation: group.manager_designation,
          }));
          setSelectedGroup(mappedGroups);
          SetGroupOptions(mappedGroups);
        } else {
          console.error("Expected an array of groups, but got:", groupres.data);
          setSelectedGroup([]); // Fallback to an empty array
        }

        const transformedAssociatedUsers = associateUsersres.data.map(
          (user) => ({
            value: user.id,
            label: user.name,
          })
        );

        setAssociatedUsers(transformedAssociatedUsers);

        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (projectId) {
      const fetchProjectDetails = async () => {
        try {
          const res = await api.get(`/api/projects/${projectId}`);
          const projectData = res.data; // Assuming this is a single project object
          const formattedProject = {
            ...projectData,
            start_date:
              projectData.start_date && projectData.start_date !== "0000-00-00"
                ? new Date(projectData.start_date)
                : null, // Set to null for invalid/missing dates
            end_date:
              projectData.end_date && projectData.end_date !== "0000-00-00"
                ? new Date(projectData.end_date)
                : null, // Set to null for invalid/missing dates
          };
          // Find the status data corresponding to the project's status_id
          const status =
            statusOptions.find((s) => s.id === formattedProject.status_id) ||
            {};
          // Set states including project status and status color
          setProject(formattedProject);
          setSelectedOwners(
            formattedProject.project_managers
              ? formattedProject.project_managers.map((m) => ({
                  label: m.label,
                  value: String(m.value),
                }))
              : []
          );
          setSelectedStatus(formattedProject.status_id || "");
          setSelectedDate(formattedProject.start_date || new Date());
          setSelectedEndDate(formattedProject.end_date || new Date());
          setSelectedCompletedDate(formattedProject.completed_date || "");
          setSelectedGroup(formattedProject.team_id || "");
          setBillingMethod(formattedProject.billingMethod || "");
          setBudgetType(formattedProject.budgetType || "");
          setCurrencyType(formattedProject.currencyType || "");

          // Set the project status and color based on the found status
          setProjectStatus(formattedProject.tags || "");
          setStatusColor(status ? status.color : "#000000"); // Default to black if not found

          setProjectNo(formattedProject.projectno || "");
          setCustomerName(formattedProject.customer_id || "");
          setLoading(false);
        } catch (error) {
          console.error("Error:", error);
          setError(error.message);
          setLoading(false);
        }
      };

      fetchProjectDetails();
    } else {
      setError("Project ID is undefined");
      setLoading(false);
    }
  }, [projectId, statusOptions]);

  //fetching admin users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/api/users");
        const activeUsers = res.data.filter(
          (member) => member.status !== "Inactive"
        );
        setUsers(activeUsers);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setError(error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  //fetching all users
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get("/api/users");
        const formattedMembers = res.data
          .filter((member) => member.status !== "Inactive") // Filter out inactive members
          .map((member) => ({
            value: member.id, // Assuming `id` is a unique identifier
            label: member.name, // Display the name as the label
          }))
          .sort((a, b) => {
            return a.label.localeCompare(b.label);
          });
        setMembers(formattedMembers);
      } catch (error) {
        if (error.response) {
          // Server responded with a status other than 200 range
          console.log("Error response:", error.response.data);
        } else if (error.request) {
          // Request was made but no response received
          console.log("Error request:", error.request);
        } else {
          // Something else caused the error
          console.log("Error message:", error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const projectName = project ? project.name : "Loading";
  const projectNumber = project ? project.projectno : "Loading";
  const projectTag = project ? project.tags : "Loading";
  const projectDescription = project ? project.description : "Loading";

  //update project details
  const handleSave = async () => {
    const formatDateForMySQL = (date) => {
      if (!date) return null; // If no date is provided, return null

      // Check if the date is already a string (in case the date is not a Date object)
      if (typeof date === "string") {
        return date;
      }

      // Check if the date is a valid Date object
      const validDate = new Date(date);
      if (isNaN(validDate)) {
        return null; // Return null if invalid date
      }

      const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );

      return localDate.toISOString().split("T")[0]; // Convert to 'YYYY-MM-DD' or null if date is not selected
    };

    const updatedProject = {
      ...project,
      manager_id: selectedOwners,
      members: associatedUsers.map((user) => user.value), // Use user IDs here for the project
      status_id: selectedStatus,
      start_date: selectedDate
        ? formatDateForMySQL(selectedDate)
        : formatDateForMySQL(project.start_date),
      end_date: selectedEndDate
        ? formatDateForMySQL(selectedEndDate)
        : formatDateForMySQL(project.end_date),
      completed_date: selectedCompletedDate,
      group: selectedGroup,
      billingMethod: billingMethod,
      budgetType: budgetType,
      currencyType: currencyType,
      status: projectStatus,
      statusColor: statusColor,
      tags: projectTag,
      customer_name: customerName,
      customer_id: customerName,
      projectno: projectNo,
    };

    try {
      const res = await api.put(`/api/projects/${projectId}`, updatedProject);
      if (res.status === 200) {
        // Only add users if associatedUsers is a valid array
        if (Array.isArray(associatedUsers) && associatedUsers.length > 0) {
          // Updated userIds key here
          const userAddResponse = await api.post(
            `/api/projects/${projectId}/users`,
            { userIds: associatedUsers.map((user) => user.value) } // Corrected here
          );

          if (userAddResponse.status === 200) {
            toast.success("Project details updated successfully", {
              position: "bottom-center",
              autoClose: 2000,
            });
          } else {
            console.error("Failed to add users:", userAddResponse);
            toast.error("Failed to add users to the project", {
              position: "bottom-center",
              autoClose: 2000,
            });
          }
        }
        handleBlur();
      } else {
        console.error("Failed to update project:", res);
        setError(res);
        toast.error("Failed to update project", {
          position: "bottom-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error(
        "Error updating project:",
        error.response ? error.response.data : error.message
      );
    }
  };

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <span className="loading-animation">LOADING...</span>
      </div>
    );

  return (
    <>
      <div
        className={
          isNavBarOpen
            ? "project-individual-details"
            : "project-individual-details-close"
        }
      >
        <ToastContainer />
        <div className="left-right-container">
          <div className="left-container">
            <div className="proj-name">
              <input
                type="text"
                className="project-name-number-input"
                style={{
                  borderRight: "none",
                  width: "6vw",
                  paddingLeft: "15px",
                  borderTopLeftRadius: "3px",
                  borderBottomLeftRadius: "3px",
                  width: `${projectNumber.length + 1}ch`,
                }}
                value={projectNumber}
                onChange={(e) =>
                  setProject({ ...project, projectno: e.target.value })
                }
                disabled={!hasPermission("projects", "update")}
              />

              <input
                type="text"
                className="project-name-number-input"
                style={{
                  borderLeft: "none",
                  width: `${projectName.length + 1}ch`,
                  borderTopRightRadius: "3px",
                  borderBottomRightRadius: "3px",
                }}
                value={projectName}
                onChange={(e) =>
                  setProject({ ...project, name: e.target.value })
                }
                disabled={!hasPermission("projects", "update")}
              />
            </div>
            <div className="information-container">
              <IoMdClose
                className={isNavBarOpen ? "close-btn" : "close-btn-close"}
                onClick={onFrameButtonClick}
              />
            </div>
          </div>
        </div>
        <div
          className="left-right-con"
          style={{ marginTop: "20px", marginLeft: "2px" }}
        >
          <div className="left-Navbar-p">
            <h3
              className={`project-individual-settings-label ${
                openSection === "projectInformation" ? "active" : ""
              }`}
              onClick={() => toggleSection("projectInformation")}
            >
              Project Information
            </h3>
            <h3
              className={`project-individual-settings-label
              ${openSection === "projectDetails" ? "active" : ""}`}
              onClick={() => toggleSection("projectDetails")}
            >
              Project Details
            </h3>
            <h3
              className={`project-individual-settings-label
                ${openSection === "budget" ? "active" : ""}`}
              onClick={() => toggleSection("budget")}
            >
              Budget
            </h3>
            <h3
              className={`project-individual-settings-label
                ${openSection === "description" ? "active" : ""}`}
              onClick={() => toggleSection("description")}
            >
              Description
            </h3>
            <h3
              className={`project-individual-settings-label
                ${openSection === "comments" ? "active" : ""}`}
              onClick={() => toggleSection("comments")}
            >
              Comments
            </h3>
          </div>
          <div className="right-container"></div>
        </div>
        {openSection === "projectInformation" && (
          <div
            className={
              isNavBarOpen ? "project-content" : "project-content-close"
            }
          >
            <div className="settings-details">
              <div className="project-owner-status-container">
                <div className="project-owner-container">
                  <h1 className="owner-heading">Owner</h1>
                  <MultiSelect
                    className="proj-group-multi"
                    value={selectedOwners}
                    onChange={(e) => setSelectedOwners(e)}
                    options={users
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((user) => ({
                        label: user.name,
                        value: String(user.id),
                      }))}
                    placeholder="Select Owner(s)"
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "pointer",
                    }}
                  />
                </div>
                <div className="project-owner-container">
                  <h1 className="project-group-heading">Project Group</h1>

                  <div
                    // onBlur={handleWrapperBlur}
                    tabIndex="0"
                    style={{ marginLeft: "-5px" }}
                  >
                    {hasPermission("projects", "update") ? (
                      <MultiSelect
                        options={options}
                        value={selectedOptions}
                        onChange={handleGroupChange}
                        // onBlur={handleBlur}
                        className="proj-group-multi"
                      />
                    ) : (
                      <ul className="project-group-list">
                        {project?.associated_groups?.map((group) => (
                          <li key={group.id} style={{ marginBottom: "10px" }}>
                            {group.name}
                          </li>
                        )) || <li>No associated groups available</li>}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              <div className="project-owner-status-container">
                <div className="project-owner-container">
                  <h1 className="start-date-heading">Start Date</h1>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="Select a date"
                    className={`start-date-input ${
                      !hasPermission("projects", "update")
                        ? "non-editable-textarea"
                        : ""
                    }`}
                    disabled={!hasPermission("projects", "update")}
                  />
                </div>
                <div className="project-owner-container">
                  <h1 className="end-date-heading">End Date</h1>
                  <DatePicker
                    selected={selectedEndDate}
                    onChange={(date) => setSelectedEndDate(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="Select a date"
                    className={`date-input ${
                      !hasPermission("projects", "update")
                        ? "non-editable-textarea"
                        : ""
                    }`}
                    disabled={!hasPermission("projects", "update")}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {openSection === "projectDetails" && (
          <div
            className={
              isNavBarOpen ? "project-content" : "project-content-close"
            }
          >
            <div className="settings-details">
              <div className="project-owner-status-container">
                <div className="project-owner-container">
                  <h1 className="proj-no-heading">Project No</h1>
                  <input
                    type="text"
                    className="tags-input"
                    placeholder="Enter Project No"
                    value={projectNumber}
                    onChange={(e) =>
                      setProject({ ...project, projectno: e.target.value })
                    }
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "auto",
                    }}
                  />
                </div>
                <div className="project-owner-container">
                  {" "}
                  <h1 className="phase-no-heading">Client Ref No</h1>
                  <input
                    type="text"
                    placeholder="Enter Client Ref No"
                    className="tags-input-crn"
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "auto",
                    }}
                  />
                </div>
              </div>
              <div className="project-owner-status-container">
                <div className="project-owner-container">
                  <h1 className="cus-name-heading">Customer Name</h1>
                  <select
                    className="select-owner-dropdown"
                    value={customerName}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      setCustomerName(selectedValue); // Update the selected customer name
                      const selectedCustomer = customers.find(
                        (customer) => customer.id === Number(selectedValue) // Match ID
                      );
                      if (selectedCustomer) {
                        setCustomerId(selectedCustomer.customerno); // Update customer ID
                      }
                    }}
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <option disabled>Select Customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="project-owner-container">
                  <h1 className="cus-id-heading">Customer ID</h1>
                  <input
                    type="text"
                    placeholder="Enter Client Ref Number"
                    value={customerId}
                    className="tags-input"
                    disabled
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "auto",
                    }}
                  />
                </div>
              </div>
              <div className="project-owner-status-container">
                {hasPermission("projects", "update") && (
                  <div className="project-owner-container">
                    <h1 className="members-heading">Project Members</h1>
                    <MultiSelect
                      options={members}
                      value={associatedUsers}
                      onChange={setAssociatedUsers}
                      className={`multi-p-select-option ${
                        !hasPermission("projects", "update")
                          ? "non-editable-textarea"
                          : ""
                      }`}
                      disabled={!hasPermission("projects", "update")}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {openSection === "budget" && (
          <div
            className={
              isNavBarOpen ? "project-content" : "project-content-close"
            }
          >
            <div className="settings-details">
              {" "}
              <div className="project-owner-status-container">
                <div className="project-owner-container">
                  <h1 className="currency-heading">Currency</h1>
                  <select
                    value={currencyType}
                    className="group-dropdown"
                    onChange={(e) => {
                      setCurrencyType(e.target.value);
                    }}
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <option value="" disabled>
                      Select Currency type
                    </option>
                    <option>IND-Indian Rupee</option>
                    <option>GDP-British pound sterling</option>
                  </select>
                </div>
                <div className="project-owner-container">
                  {" "}
                  <h1 className="budget-type-heading">Budget type</h1>
                  <select
                    className="group-dropdown"
                    value={budgetType}
                    onChange={(e) => {
                      setBudgetType(e.target.value);
                    }}
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <option value="" disabled>
                      Select Budget type
                    </option>
                    <option>Based on Project Amount</option>
                    <option>Based on Project Hours</option>
                    <option>Based on Task Amount</option>
                    <option>Based on Task Hours</option>
                    <option>Based on User Amount</option>
                    <option>Based on User Hours</option>
                  </select>
                </div>
              </div>
              <div className="project-owner-status-container">
                <div className="project-owner-container">
                  <h1 className="billing-heading">Billing Method</h1>
                  <select
                    value={billingMethod}
                    onChange={(e) => {
                      setBillingMethod(e.target.value);
                    }}
                    className="group-dropdown"
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "pointer",
                      marginLeft: "78px",
                    }}
                  >
                    <option value="" disabled>
                      Select Billing type
                    </option>
                    <option>Based on Project Hours</option>
                    <option>Based on Staff Hours</option>
                    <option>Based on Task/Issue Hours</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
        {openSection === "description" && (
          <div
            className={
              isNavBarOpen ? "project-content" : "project-content-close"
            }
          >
            <div className="settings-details">
              <div className="project-owner-status-container">
                <div
                  className="project-owner-container"
                  style={{ flexDirection: "column" }}
                >
                  <h1 className="description-heading">Description</h1>
                  <textarea
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "auto",
                    }}
                    type="text-area"
                    placeholder="No Description available"
                    className="description-para"
                    value={projectDescription}
                    onChange={(e) =>
                      setProject({ ...project, description: e.target.value })
                    }
                    disabled={!hasPermission("projects", "update")}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {openSection === "comments" && (
          <div
            className={
              isNavBarOpen ? "project-content" : "project-content-close"
            }
          >
            <div className="settings-details">
              <div className="project-owner-status-container">
                <div
                  className="project-owner-container"
                  style={{ flexDirection: "column" }}
                >
                  <h1 className="description-heading">Comments</h1>
                  <textarea
                    type="text-area"
                    placeholder="No Comments"
                    className="description-para"
                    height={200}
                    width={300}
                    disabled={!hasPermission("projects", "update")}
                    style={{
                      cursor: !hasPermission("projects", "update")
                        ? "not-allowed"
                        : "auto",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={
          isNavBarOpen
            ? "individual-btn-container"
            : "individual-btn-container-close"
        }
      >
        <button
          className="save-project-btn"
          onClick={() => {
            handleSave();
            // handleBlur();
          }}
          style={{
            cursor:
              !hasPermission("projects", "update") ||
              selectedOwners.length === 0
                ? "not-allowed"
                : "pointer",
          }}
          disabled={
            !hasPermission("projects", "update") || selectedOwners.length === 0
          }
        >
          Save
        </button>
        <button className="cancel-project-btn" onClick={onFrameButtonClick}>
          Cancel
        </button>
        <p className="layout-error-msg">{error}</p>
      </div>
    </>
  );
};

export default ProjectLayout;
